import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import { Loader2, Send, Inbox, FileText, MessageSquare } from "lucide-react";

const STATUS_META = {
  pending:     { label: "În așteptare", fg: "#a16207", bg: "#fef9c3" },
  in_progress: { label: "În lucru",     fg: "#1d4ed8", bg: "#dbeafe" },
  resolved:    { label: "Rezolvată",    fg: "#166534", bg: "#dcfce7" },
  rejected:    { label: "Respinsă",     fg: "#b91c1c", bg: "#fee2e2" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { label: status, fg: "#374151", bg: "#f4f4f5" };
  return (
    <span className="inline-flex items-center px-2.5 h-6 text-[11px] font-semibold rounded-full"
      style={{ background: m.bg, color: m.fg }} data-testid={`request-status-${status}`}>{m.label}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export default function Requests() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/staff/requests/mine");
      setItems(data || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function submit() {
    if (!docType.trim()) { toast.error("Completează tipul documentului solicitat"); return; }
    setBusy(true);
    try {
      await api.post("/staff/requests", { document_type: docType.trim(), details: details.trim() });
      toast.success("Cerere trimisă către HR / administrativ");
      setDocType(""); setDetails("");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1000px] mx-auto">
        <header className="pb-8 pt-2">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-3">Personal</div>
          <h1 className="text-[38px] md:text-[46px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
            Cereri
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-muted max-w-xl">
            Trimite cereri de documente către HR / administrativ și urmărește statusul lor.
          </p>
        </header>

        {/* Formular cerere nouă */}
        <div className="surface p-6 md:p-8 mb-8" data-testid="new-request-form">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-4">Cerere nouă</div>
          <label className="block mb-5">
            <span className="block text-[12px] font-medium text-ink-muted mb-2">Tip document solicitat *</span>
            <input
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              data-testid="request-document-type"
              placeholder="Ex: Adeverință de salariat, Adeverință vechime, Copie CIM..."
              className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-[14px] outline-none focus:border-ink" />
          </label>
          <label className="block mb-6">
            <span className="block text-[12px] font-medium text-ink-muted mb-2">Detalii (opțional)</span>
            <textarea
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              data-testid="request-details"
              placeholder="Adaugă orice detaliu util pentru HR (ex: scopul documentului, termen dorit)."
              className="w-full border border-neutral-200 rounded-lg p-3 text-[14px] outline-none focus:border-ink resize-none" />
          </label>
          <div className="flex justify-end">
            <Button variant="primary" size="md" icon={Send} loading={busy}
              onClick={submit} disabled={!docType.trim()} data-testid="request-submit">
              Trimite cererea
            </Button>
          </div>
        </div>

        {/* Istoric cereri */}
        <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
          Istoric cereri
        </div>
        <div className="surface overflow-hidden" data-testid="requests-history">
          {loading ? (
            <div className="py-20 flex items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center text-ink-muted">
              <Inbox className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
              <div className="text-[15px]">Nu ai trimis nicio cerere încă.</div>
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {items.map((r) => (
                <div key={r.id} className="px-5 py-4" data-testid={`request-row-${r.id}`}>
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-full flex items-center justify-center bg-neutral-100 text-ink-muted shrink-0">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2.5 mb-1 flex-wrap">
                        <div className="text-[15px] font-medium text-ink">{r.document_type}</div>
                        <StatusPill status={r.status} />
                      </div>
                      <div className="text-[13px] text-ink-muted">Trimisă {fmtDate(r.created_at)}</div>
                      {r.details && <div className="text-[13px] text-ink mt-2">{r.details}</div>}
                      {r.hr_response && (
                        <div className="mt-3 p-3 rounded-lg bg-neutral-50 border border-neutral-100">
                          <div className="flex items-center gap-2 text-[11px] font-medium text-ink-muted mb-1">
                            <MessageSquare className="h-3.5 w-3.5" /> Răspuns HR{r.resolved_by_name ? ` · ${r.resolved_by_name}` : ""}
                          </div>
                          <div className="text-[13px] text-ink">{r.hr_response}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PortalLayout>
  );
}
